<template>
  <div class="q-px-xl">
    <c-q-separator class="q-pb-lg" />

    <!-- BUSCA -->
    <div id="div-pesquisa-campanha" class="row q-pb-lg">
      <div class="q-pb-xs">
        <span class="midia-app-label-lg">Pesquise um Cadastro</span>
      </div>

      <div class="row fit no-wrap justify-between items-end">
        <div class="col-grow">
          <q-input
            id="searchbar"
            v-model="filterTerms"
            @update:model-value="onSearchInput"
            color="globalAppPrimaryColor"
            bg-color="globalAppDefaultBackgroundColor"
            outlined
            dense
            placeholder="Busque um Cadastro"
          >
            <template #prepend>
              <q-icon
                name="search"
                class="q-pa-sm"
                style="
                  background: var(--globalAppSecondaryColor4);
                  padding: 6px;
                  position: relative;
                  left: -11px;
                  border-right: 1px solid var(--globalAppSecondaryColor2);
                "
              />
            </template>

            <template #append>
              <q-btn
                id="btn-show-filterbox"
                unelevated
                outlined
                no-caps
                @click="onFilterBtnClick"
              >
                <q-icon size="16px">
                  <img class="svg-icon" src="@/assets/icons/icon_filter.svg" />
                </q-icon>
                <span class="midia-app-label-lg q-pl-sm">Filtrar</span>
              </q-btn>
            </template>
          </q-input>
        </div>
      </div>
    </div>

    <span class="midia-app-label-sm">Cadastros encontrados</span>

    <!-- TABELA -->
    <div class="row justify-center q-mt-md">
      <div style="width: 100%">
        <q-table
          class="midia-app-table"
          row-key="id"
          flat
          separator="horizontal"
          :rows="filteredRows"
          :columns="qTableConsultaColumns"
          v-model:pagination="pagination"
          :rows-per-page-options="[5, 10, 25, 50, 100]"
          no-data-label="Nenhum registro encontrado!"
          rows-per-page-label="Linhas por página:"
          :dense="$q.screen.lt.md"
        >
          <template #header="props">
            <q-tr :props="props">
              <q-th
                v-for="col in props.cols"
                :key="col.name"
                :props="props"
                style="background: var(--globalAppSecondaryColor4); border: 0"
              >
                <span class="midia-app-title-header-table-lg">
                  {{ col.label }}
                </span>
              </q-th>
            </q-tr>
          </template>

          <template #body="props">
            <q-tr :props="props" class="table-body-rows">
              <q-td v-for="col in qTableConsultaColumns"
                    :key="col.name"
                    :props="props">
                <span class="midia-app-label-lg">
                  {{ props.row[col.field] }}
                </span>
              </q-td>
            </q-tr>
          </template>

          <template #bottom>
            <div class="row full-width items-center justify-end q-pa-sm q-gutter-sm">

              <span class="midia-app-label-sm">Linhas por página:</span>
              <q-select
                v-model="pagination.rowsPerPage"
                :options="[5, 10, 25, 50, 100]"
                dense outlined
                style="width: 70px"
                @update:model-value="pagination.page = 1"
              />

              <span class="midia-app-label-sm">
                {{ (pagination.page - 1) * pagination.rowsPerPage + 1 }}-{{ Math.min(pagination.page * pagination.rowsPerPage, filteredRows.length) }}
                de {{ filteredRows.length }}
              </span>

              <!-- ✅ Primeira página -->
              <q-btn flat dense round icon="first_page"
                :disable="pagination.page === 1"
                @click="pagination.page = 1"
              />

              <!-- Anterior -->
              <q-btn flat dense round icon="chevron_left"
                :disable="pagination.page === 1"
                @click="pagination.page--"
              />

              <!-- Próxima -->
              <q-btn flat dense round icon="chevron_right"
                :disable="pagination.page * pagination.rowsPerPage >= filteredRows.length"
                @click="pagination.page++"
              />

              <!-- ✅ Última página -->
              <q-btn flat dense round icon="last_page"
                :disable="pagination.page * pagination.rowsPerPage >= filteredRows.length"
                @click="pagination.page = Math.ceil(filteredRows.length / pagination.rowsPerPage)"
              />

            </div>
          </template>
        </q-table>

        <!-- EXPORTAR -->
        <div class="row justify-end q-mt-sm">
          <div class="text-right">
            <span class="midia-app-label-sm">Exportar Lista</span>
            <div>
              <q-btn
                size="sm"
                outline
                color="primary"
                label="Download"
                @click="exportToCsv"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <c-q-separator class="q-my-md" />
  </div>

  <ModalFilterConsulta />

</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import { usePagesUtilStore } from '@/stores/pagesUtilStore'
import { useInstitutionalStore } from '@/stores/institutionalStore'
import { quasarCustomQTableColumns } from '@/types/quasarCustomQTableColumn'
import ModalFilterConsulta from '@/views/layout/modalsConsulta/ModalFilterConsulta.vue'
import { useRedeSubredeStore } from '@/stores/redeSubredeStore'
import { CONST_INSTITUTIONAL_STATUS } from '@/static/constantsInstitutional'

const redeSubredeStore = useRedeSubredeStore()
const $q = useQuasar()
const pagesUtilStore = usePagesUtilStore()
const institutionalStore = useInstitutionalStore()

/* =========================
 * FILTRO
 * ========================= */
const filterTerms = ref('')

/* =========================
 * PAGINAÇÃO
 * ========================= */
const pagination = ref({
  page: 1,
  rowsPerPage: 10,
  sortBy: 'id',
  descending: false
})

const formatTelas = (telas: string) => {
  if (!telas) return '-'
  const map: Record<string, string> = {
    'INICIAL': 'Iniciais',
    'AGUARDE': 'Aguarde',
    'ABERTURA': 'Abertura'
  }
  return telas.split(',').map(t => map[t.trim()] ?? t).join(', ')
}

const formatDateBr = (date?: string | null) => {
  if (!date) return '-'
  const [year, month, day] = date.split('-')
  return `${day}/${month}/${year}`
}

/* =====================
 * SEARCH
 * ===================== */
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const onSearchInput = async (value: string | number | null) => {
  if (searchTimeout) clearTimeout(searchTimeout)

  searchTimeout = setTimeout(async () => {
    const term = value ? String(value).trim() : ''

    if (term) {
      institutionalStore.institutionalFilter = { nome: term }
    } else {
      institutionalStore.resetInstitutionalFilter()
    }
    await institutionalStore.fetchInstitutionalScreens()
  }, 400)
}


/* =========================
 * MAPPER BACK → TABLE
 * ========================= */
const rows = computed(() =>
  institutionalStore.institutionalScreens.map(item => {
    const rede = item.redes?.[0]

    // ✅ AQUI - antes do return
    const subredesNomes = item.redes?.map(r => {
      const info = redeSubredeStore.subredes.find(
        s => s.codSubredeAutoAtndto === r.codSubrede
      )
      return info?.nomSubredeAutoAtndto ?? r.codSubrede
    }).join(', ') ?? '-'

    return {
      id: item.id,
      register: item.nome,
      networkCode: rede?.codRede ?? '-',
      network: rede?.codRede ? 'Banco24Horas' : '-',
      subnetCode: item.redes?.map(r => r.codSubrede).join(', ') ?? '-',
      subnet: subredesNomes,  // ✅ todos os nomes
      pc: item.pcsIds?.length ? item.pcsIds.join(', ') : '-',
      startDate: formatDateBr(item.dtIniVigencia),
      endDate: formatDateBr(item.dtFimVigencia),
      screen: formatTelas(item.telasCadastradas),
      profile: item.perfil ?? '-',
      granularity: item.pcsIds?.length
                    ? 'Por PC'
                    : item.redes?.length === redeSubredeStore.subredes.length
                      ? 'Por Rede'
                      : item.redes?.length
                        ? 'Por Subrede'
                        : '-',
      status: item.status,
    }
  })
)

/* =========================
 * FILTRO LOCAL
 * ========================= */
const filteredRows = computed(() => {
  const term = filterTerms.value.toLowerCase()

  return rows.value.filter(row => {
    // ✅ filtro da barra de busca
    const matchSearch = !term || Object.values(row).some(val =>
      String(val ?? '').toLowerCase().includes(term)
    )

    // ✅ filtro de granularidade do modal
    const matchGranularity = !institutionalStore.institutionalFilter.granularidade
      || row.granularity === institutionalStore.institutionalFilter.granularidade

    return matchSearch && matchGranularity
  })
})

/* =========================
 * COLUNAS
 * ========================= */
const qTableConsultaColumns: quasarCustomQTableColumns[] = [
  { name: 'id', label: 'ID', field: 'id', align: 'center' },
  { name: 'register', label: 'Cadastro', field: 'register', align: 'left' },
  { name: 'networkCode', label: 'Cód. Rede', field: 'networkCode', align: 'left' },
  { name: 'network', label: 'Rede', field: 'network', align: 'left' },
  { name: 'subnetCode', label: 'Cód. Subrede', field: 'subnetCode', align: 'left' },
  { name: 'subnet', label: 'Subrede', field: 'subnet', align: 'left' },
  { name: 'pc', label: 'PC', field: 'pc', align: 'left' },
  { name: 'startDate', label: 'Início', field: 'startDate', align: 'left' },
  { name: 'endDate', label: 'Fim', field: 'endDate', align: 'left' },
  { name: 'screen', label: 'Tela', field: 'screen', align: 'left' },
  { name: 'profile', label: 'Perfil', field: 'profile', align: 'left' },
  { name: 'granularity', label: 'Granularidade', field: 'granularity', align: 'left' }
]

const getStatusLabel = (statusId: number | string) => {
  const status = Object.values(CONST_INSTITUTIONAL_STATUS)
    .find(s => s.id === statusId)

  return status?.description ?? statusId
}

/* =========================
 * EXPORT CSV
 * ========================= */
const exportToCsv = () => {
  const headers = [
    'ID',
    'Cadastro',
    'Cód. Rede',
    'Rede',
    'Cód. Subrede',
    'Subrede',
    'PC',
    'Início',
    'Fim',
    'Tela',
    'Perfil',
    'Granularidade',
    'Status'
  ].join(';')

  const body = filteredRows.value.map(row => [
    `"${row.id ?? ''}"`,
    `"${row.register ?? ''}"`,
    `"${row.networkCode ?? ''}"`,
    `"${row.network ?? ''}"`,
    `"${row.subnetCode ?? ''}"`,
    `"${row.subnet ?? ''}"`,
    `"${row.pc ?? ''}"`,
    `"${row.startDate ?? ''}"`,
    `"${row.endDate ?? ''}"`,
    `"${row.screen ?? ''}"`,
    `"${row.profile ?? ''}"`,
    `"${row.granularity ?? ''}"`,
    `"${getStatusLabel(row.status)}"`
  ].join(';'))

  const csv = [headers, ...body].join('\n')

  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;'
  })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = 'cadastros_telas_institucionais.csv'
  link.click()

  URL.revokeObjectURL(url)
}


/* =========================
 * FILTRO MODAL
 * ========================= */
const onFilterBtnClick = () => {
  pagesUtilStore.showModalFilterInstitutional = true
}

/* =========================
 * INIT
 * ========================= */
onMounted(async () => {
  try {
    pagesUtilStore.showLoading = true
    await redeSubredeStore.carregarSubredes()
    await institutionalStore.fetchInstitutionalScreens()
  } finally {
    pagesUtilStore.showLoading = false
  }
})
</script>

<style scoped>
#btn-show-filterbox {
  margin-right: -11px;
  height: 97%;
  border-left: 1px solid rgb(186, 186, 186);
}

.midia-app-table .q-td {
  padding: 10px 12px;
}

.midia-app-table .q-th {
  padding: 12px 12px;
}

.table-body-rows {
  height: 48px;
}

.midia-app-table .q-td span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 180px;
}

.midia-app-table thead tr {
  height: 44px;
}

.midia-app-table .q-th span {
  font-weight: 600;
}

.midia-app-table tbody tr:hover {
  background-color: #f5f5f5;
}

</style>